import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { VscChevronLeft, VscChevronRight } from 'react-icons/vsc';
import { getSld } from '../../api/sld';
import { updateProject } from '../../api/projects';
import PrintButton from '../common/PrintButton';
import { buildSld } from '../../utils/sldLayout';
import { SldSvg, buildSldPdf } from '../../utils/sldRender';
import './Reports.css';

const WRITE_ROLES = ['admin', 'engineer', 'manager', 'foreman'];
const PRINT_ROLES = ['admin', 'engineer', 'manager', 'foreman', 'team-leader'];
const TB_FIELDS = [
  { key: 'drawingNo', label: 'Drawing no.' },
  { key: 'revision',  label: 'Rev.' },
  { key: 'drawnBy',   label: 'Drawn' },
  { key: 'checkedBy', label: 'Checked' },
];

function SingleLineDiagram({ project, role, onOpenTab }) {
  const [data, setData]   = useState(null);
  const [error, setError] = useState('');
  const [sheet, setSheet] = useState(0);
  const [tb, setTb]       = useState({});
  const canEdit = WRITE_ROLES.includes(role);

  const load = useCallback(() => {
    if (!project?._id) return;
    getSld(project._id)
      .then(r => { setData(r.data); setTb(r.data.project?.sldInfo || {}); setError(''); })
      .catch(e => setError(e?.response?.data?.msg || 'Could not load the diagram.'));
  }, [project?._id]);
  useEffect(() => { load(); }, [load]);

  const info = useMemo(() => {
    const p = data?.project || {};
    return {
      ...tb,
      client:  p.client || '',
      vessel:  p.shipName || p.name || '',
      project: p.name || '',
      nb:      p.newbuildingNo || '',
      date:    new Date().toLocaleDateString('da-DK'),
    };
  }, [data, tb]);

  const pages = useMemo(() => (data ? buildSld(data, info) : []), [data, info]);
  useEffect(() => { if (sheet >= pages.length && pages.length) setSheet(0); }, [pages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveTb = (key, value) => {
    const next = { ...tb, [key]: value };
    setTb(next);
    updateProject(project._id, { sldInfo: next }).catch(() => setError('Could not save the title block.'));
  };

  // Print options: one tick per sheet group (overview + each switchboard), plus the cable / kW rows
  const groups = useMemo(() => {
    const seen = new Map();
    for (const p of pages) if (!seen.has(p.key)) seen.set(p.key, p.key === 'overview' ? 'Overview' : p.title.replace(/ \(\d+\/\d+\)$/, '').split(' — ')[0]);
    return [...seen.entries()];
  }, [pages]);

  const printPdf = (keys) => {
    const opts = { cables: keys.includes('_cables'), kw: keys.includes('_kw') };
    const all = buildSld(data, info, opts);
    return buildSldPdf(all.filter(p => keys.includes(p.key)));
  };

  const page = pages[sheet];

  return (
    <div className="module-wrap">
      <div className="module-toolbar report-toolbar">
        <span className="module-toolbar-title">Single Line Diagram</span>
        {pages.length > 0 && (
          <div className="sld-nav">
            <button className="btn-icon" title="Previous sheet" disabled={sheet === 0} onClick={() => setSheet(s => s - 1)}><VscChevronLeft size={16} /></button>
            <select className="report-sb-select" value={sheet} onChange={e => setSheet(Number(e.target.value))}>
              {pages.map((p, i) => <option key={i} value={i}>{i + 1}. {p.title}</option>)}
            </select>
            <button className="btn-icon" title="Next sheet" disabled={sheet >= pages.length - 1} onClick={() => setSheet(s => s + 1)}><VscChevronRight size={16} /></button>
          </div>
        )}
        {PRINT_ROLES.includes(role) && data && (
          <PrintButton
            title={`Single line diagram — ${project?.name ?? ''}`}
            storageKey="sld"
            rows={[]}
            onOpenTab={onOpenTab}
            columns={[
              ...groups.map(([key, label]) => ({ key, label: `Sheet: ${label}` })),
              { key: '_cables', label: 'Cable row' },
              { key: '_kw',     label: 'kW row' },
            ]}
            chartPdf={printPdf}
          />
        )}
      </div>
      <div className="sld-tb">
        {TB_FIELDS.map(f => (
          <label key={f.key}>
            {f.label}
            <input defaultValue={tb[f.key] || ''} key={`${f.key}-${data ? 'l' : 'x'}`} disabled={!canEdit}
              onBlur={e => { if ((e.target.value || '') !== (tb[f.key] || '')) saveTb(f.key, e.target.value.trim()); }}
              onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }} />
          </label>
        ))}
        <span className="report-muted sld-hint">
          Drawn from the switchboards, breakers, cables and items, plus Load Calculation → Supply setup (sources, feeds, incomers, bus ties).
        </span>
      </div>
      <div className="module-body sld-body">
        {error && <div className="lc-error">{error}</div>}
        {!data && !error && <div className="empty-state">Loading…</div>}
        {page && <SldSvg page={page} className="sld-sheet" />}
      </div>
    </div>
  );
}

export default SingleLineDiagram;
